import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Button } from '@/shared/components';
import {
  AlertTriangleIcon,
  BarChart3Icon,
  ClipboardListIcon,
  GitCompareIcon,
  LayoutDashboardIcon,
  SlidersHorizontalIcon,
  ChevronDownIcon,
  FileTextIcon,
  FileCheckIcon,
  Building2Icon,
  MapPinIcon,
} from '@/shared/ui/Icons';
import { GraduationCap, Settings, TrendingUp } from 'lucide-react';
import { hasFullDashboardAccess } from '@/features/auth/roleAccess';

const REPORTS_ROUTES_FULL = [
  { path: '/reports/manage-classification', label: 'Manage Classification', icon: FileCheckIcon },
  { path: '/reports/school-profile', label: 'School Profile Dashboard', icon: Building2Icon },
  { path: '/reports/risk-triage', label: 'Risk Triage Report', icon: AlertTriangleIcon },
  { path: '/reports/teacher-equity', label: 'Teacher Equity Report', icon: SlidersHorizontalIcon },
  { path: '/reports/regional-comparisons', label: 'Regional comparisons', icon: MapPinIcon },
  { path: '/reports/tuition-fee-benchmark', label: 'Tuition Fee Benchmark', icon: GraduationCap },
  { path: '/reports/school-size-cost', label: 'School Size vs Cost', icon: TrendingUp },
  { path: '/reports/compliances', label: 'Compliances', icon: ClipboardListIcon },
  { path: '/reports/group-comparisons', label: 'Group Comparisons', icon: GitCompareIcon },
] as const;

const REPORTS_ROUTES_SCHOOL_ONLY = [
  { path: '/reports/school-profile', label: 'School Profile Dashboard', icon: Building2Icon },
] as const;

function isReportsPath(pathname: string): boolean {
  return pathname.startsWith('/reports');
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const currentPath = location.pathname;
  const fullAccess = hasFullDashboardAccess(user?.roles);
  const reportsNavItems = fullAccess ? REPORTS_ROUTES_FULL : REPORTS_ROUTES_SCHOOL_ONLY;

  const isReportsRoute = isReportsPath(currentPath);
  const [reportsOpen, setReportsOpen] = useState(isReportsRoute || !fullAccess);

  useEffect(() => {
    if (isReportsRoute) setReportsOpen(true);
    else if (!fullAccess) setReportsOpen(true);
    else setReportsOpen(false);
  }, [currentPath, isReportsRoute, fullAccess]);

  const displayName = user?.name ?? user?.userName ?? '';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#f0f4f8]">
      <aside className="flex w-56 shrink-0 flex-col border-r border-slate-200/80 bg-white">
        <div className="flex h-16 items-center gap-3 border-b border-slate-200/80 px-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <BarChart3Icon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight text-slate-900">Mehe Reporting</h1>
            <p className="text-xs text-slate-500">Dashboards</p>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 p-3" aria-label="Main navigation">
          {fullAccess && (
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                currentPath === '/'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <LayoutDashboardIcon
                className={`size-4 shrink-0 ${currentPath === '/' ? 'text-indigo-600' : 'text-slate-400'}`}
              />
              Main Dashboard
            </button>
          )}

          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => setReportsOpen((o) => !o)}
              aria-expanded={reportsOpen}
              aria-controls="reports-submenu"
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                isReportsRoute
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center gap-3">
                <FileTextIcon
                  className={`size-4 shrink-0 ${isReportsRoute ? 'text-indigo-600' : 'text-slate-400'}`}
                />
                Reports
              </span>
              <ChevronDownIcon
                className={`size-4 shrink-0 text-slate-400 transition-transform ${reportsOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {reportsOpen && (
              <div id="reports-submenu" className="ml-2 flex flex-col gap-0.5 border-l-2 border-indigo-100 py-0.5 pl-2">
                {reportsNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = currentPath === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium transition-colors ${
                        active
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <Icon className={`size-4 shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {fullAccess && (
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                currentPath === '/settings'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Settings
                className={`size-4 shrink-0 ${currentPath === '/settings' ? 'text-indigo-600' : 'text-slate-400'}`}
              />
              Settings
            </button>
          )}
        </nav>
      </aside>
      <main className="min-w-0 flex-1 flex flex-col overflow-auto">
        <header className="flex h-14 shrink-0 items-center justify-end gap-3 border-b border-slate-200/80 bg-white px-6">
          {displayName && (
            <span className="text-sm text-slate-600">{displayName}</span>
          )}
          <Button variant="secondary" onClick={handleLogout}>
            Logout
          </Button>
        </header>
        <div className="mx-auto w-full max-w-[1920px] flex-1 px-6 py-6 lg:px-8 xl:px-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
